import { DownOutlined, EyeInvisibleOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Tabs, Tooltip } from 'antd'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js'
import React from 'react'
import { Bar, Line } from 'react-chartjs-2'
import { MalindexBesok, MalindexConfig, MalindexMal } from '../../../api/malindex/malindexApi'
import { BLUE, CYAN, GREEN, ORANGE, PURPLE, RED } from '../../benchmarking/util/colors'
import NyKartlaggningModal from './NyKartlaggningModal'
import { besoksEtikett, formateraDatum, formateraDatumTid, SVARANDE_ETIKETT } from '../visning'
import GammaltUtkastModal, { GAMMALT_UTKAST_DAGAR, utkastAlderIDagar } from './GammaltUtkastModal'
import RaderaBesokModal from './RaderaBesokModal'
import {
  aktivaMal,
  BehovNyttaRad,
  behovVsNytta,
  nyaMalVid,
  indexUtveckling,
  kronologiskt,
  pagaendePeriod,
  periodNummer,
  periodbrytningar,
  raknade,
  senasteIndexPar,
} from '../malindexCalc'

/**
 * OversiktHub – nivå 1 efter att en brukare valts.
 *
 * Tre flikar: besökslistan, Målindex över tid, och behov mot nytta per område.
 *
 * SAMTALSLÄGE: när det är på döljs alla siffror och grafer. Indexet ska inte synas
 * för den enskilde under samtalet (spec 2.4), och handläggaren sitter med plattan
 * mellan sig och brukaren. Listan över besök syns fortfarande – den avslöjar inget.
 */

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ChartTooltip, Legend)

export interface OversiktHubProps {
  config: MalindexConfig
  besok: MalindexBesok[]
  samtalslage: boolean
  onNyttKartlaggningsbesok: () => void
  onNyUppfoljning: () => void
  /** Återupptar det pausade besöket. */
  onAterupptaUtkast: () => void
  /** Raderar det pausade besöket. Raden arkiveras, den tas inte bort på riktigt. */
  onRaderaBesok: () => void
  /** Öppnar ett enskilt besök i sin egen vy. */
  onOppnaBesok: (id: number) => void
  /** Fyller i ÄrendeID på ett besök som sparats utan. */
  onKompletteraProcessId: (id: number, processId: string) => Promise<void>
}

const { TabPane } = Tabs

/**
 * En färg per omgång i indexlinjen. Valda för att skilja sig tydligt från varandra –
 * intilliggande omgångar ska aldrig kunna förväxlas. Räcker de inte till börjar de om.
 */
const PERIODFARGER = [BLUE, GREEN, ORANGE, PURPLE, CYAN, RED] as const

/**
 * Målets text i översikten: den FORMULERADE målformuleringen, inte brukarens egna ord.
 *
 * De egna orden hör hemma i samtalet – de läses upp ordagrant i uppföljningen, för
 * det är formuleringen brukaren känner igen. Översikten är handläggarens vy, och där
 * är det målet som ska gå att läsa av.
 *
 * De egna orden är kvar som reserv: målet går att spara med bara det ena fältet ifyllt,
 * och en tom rad vore sämre än fel sorts text.
 */
const maltextFor = (m: MalindexMal) => m.maltext.trim() || m.egnaOrd.trim()

/**
 * Grafrubrik enligt flaggvyernas mönster (Utförare > Flaggöversikt): centrerad h1
 * som namnger jämförelsen, inte bara innehållet.
 */
const GrafRubrik: React.FC<{ titel: string }> = ({ titel }) => (
  <h1 className="m-0 mb-3 text-center" style={{ fontSize: 20 }}>
    {titel}
  </h1>
)

/**
 * Egen legend i stället för chart.js egen. Ligger UNDER grafen, efter x-axelns
 * etiketter – som absolutpositionerad uppe till höger lade den sig över staplarna.
 */
const GrafLegend: React.FC<{ poster: { farg: string; kant: string; text: string }[] }> = ({ poster }) => (
  <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2">
    {poster.map((p) => (
      <div key={p.text} className="flex items-center gap-2">
        <span
          style={{ width: 14, height: 14, backgroundColor: p.farg, border: `1.5px solid ${p.kant}`, borderRadius: 2 }}
          className="shrink-0"
        />
        <span className="text-sm text-gray-700">{p.text}</span>
      </div>
    ))}
  </div>
)

/**
 * Vilka mätningar grafen bygger på. Ligger sist, under legenden: den behövs för att
 * tolka det man just sett, inte för att förstå vad man ska titta på.
 */
const GrafKalla: React.FC<{ text: string }> = ({ text }) => (
  <p className="m-0 mt-3 text-center text-sm text-gray-500">{text}</p>
)

/** Ersätter en graf eller siffra när samtalsläget är på. */
const DoltISamtalslage: React.FC<{ vad: string }> = ({ vad }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
    <EyeInvisibleOutlined className="text-2xl text-gray-300" />
    <p className="text-base font-medium text-gray-600">{vad} är dolt i samtalsläge</p>
    <p className="max-w-md text-sm text-gray-500">
      Resultat och siffror visas inte medan besöket pågår. Stäng av samtalsläget efter besöket, när brukaren inte
      längre ser skärmen.
    </p>
  </div>
)

/** Inline-komplettering av ÄrendeID direkt i listraden. */
const KompletteraProcessId: React.FC<{ besokId: number; onSpara: (id: number, processId: string) => Promise<void> }> = ({
  besokId,
  onSpara,
}) => {
  const [oppen, setOppen] = React.useState(false)
  const [varde, setVarde] = React.useState('')
  const [sparar, setSparar] = React.useState(false)

  if (!oppen) {
    return (
      <Tooltip title="Klicka för att fylla i ÄrendeID">
        <button
          type="button"
          onClick={() => setOppen(true)}
          className="rounded-sm border border-amber-400 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
        >
          ÄrendeID saknas
        </button>
      </Tooltip>
    )
  }

  // 44px är tap-målet vi håller genom hela flödet, och 16px hindrar iOS Safari från
  // att zooma in när fältet fokuseras.
  const falthojd = { height: 44, fontSize: 16 }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Input
        autoFocus
        value={varde}
        onChange={(e) => setVarde(e.target.value)}
        onPressEnter={async () => {
          if (!varde.trim()) return
          setSparar(true)
          await onSpara(besokId, varde.trim())
          setSparar(false)
        }}
        placeholder="t.ex. 10101"
        style={{ width: 160, ...falthojd }}
      />
      <Button
        type="primary"
        loading={sparar}
        disabled={!varde.trim()}
        style={falthojd}
        onClick={async () => {
          setSparar(true)
          await onSpara(besokId, varde.trim())
          setSparar(false)
        }}
      >
        Spara
      </Button>
      <Button style={falthojd} onClick={() => setOppen(false)}>
        Avbryt
      </Button>
    </span>
  )
}

/**
 * Besokshistoriken som en tidslinje: aldst overst, nasta steg dar vagen tar slut.
 *
 * Rollen harleds ur innehallet i stallet for att visa vilka falt som ar ifyllda.
 * Ett besok med nyttoskattning ar en uppfoljning, annars ar det kartlaggningen -
 * "Behov"/"Nytta" beskrev datamodellen, inte vad handlaggaren faktiskt gjorde.
 *
 * Raden halls kort. Det som skedde under besoket star i expansionen, dar det finns
 * plats att skriva ut viktningskedjan i klartext. Prioriterade omraden lagras men
 * visas inte - de ar underlag for analys, inte for den har vyn.
 */
interface AnnoteratBesok {
  besok: MalindexBesok
  etikett: string
  harNyBehov: boolean
  /** Antal mål som FORMULERADES vid besöket – inte hur många som gällde efteråt. */
  formuleradeMal: number
}

const annotera = (besok: MalindexBesok[]): AnnoteratBesok[] => {
  const sorterade = kronologiskt(besok)
  return sorterade.map((b, i) => {
    const harNytta = !!b.nytta_svar && Object.keys(b.nytta_svar).length > 0
    const harBehov = !!b.behov_svar && Object.keys(b.behov_svar).length > 0
    return {
      besok: b,
      /*
        Målen som FORMULERADES vid besöket, inte de som gällde efteråt.

        Tidslinjen är en lista över händelser. Ett löpande tillstånd i den gav ordet
        "mål" två betydelser på samma sida – raden menade "gällande", besöksvyn menade
        "formulerade" – och en siffra i en rubriklös kolumn går inte att göra entydig
        med en etikett.

        Tillståndet finns där det hör hemma: rutan "Aktiva mål" högst upp.
      */
      formuleradeMal: nyaMalVid(b, sorterade).length,
      // Roll och kanal i samma etikett: "Uppföljande telefonsamtal". Ingen numrering –
      // tidslinjens ordning och tidpunkten skiljer raderna åt ändå.
      //
      // Rollen kommer ur samma källa som perioden, alltså vad handläggaren valde att
      // starta. Etiketten och periodgränsen får inte kunna säga olika saker.
      etikett: besoksEtikett(b),
      harNyBehov: harNytta && harBehov,
    }
  })
}

/**
 * Vad som skedde under besoket, i klartext.
 *
 * Varje mening ar tackt av data. Obesvarat redovisas som obesvarat i stallet for att
 * gissas - en genererad sammanfattning lases som ett faktum, och da far den inte
 * pasta mer an den vet.
 */
const BesokDetaljer: React.FC<{
  rad: AnnoteratBesok
  alla: AnnoteratBesok[]
  samtalslage: boolean
  onOppna: (id: number) => void
}> = ({ rad, alla, samtalslage, onOppna }) => {
  const b = rad.besok
  const harNytta = !!b.nytta_svar && Object.keys(b.nytta_svar).length > 0
  const vagtMot = b.index_viktat_av ? alla.find((r) => r.besok.id === b.index_viktat_av) : null

  const uppfoljda = b.mal_uppfoljning ?? []
  const behallna = uppfoljda.filter((u) => u.behall === true).length
  const avslutade = uppfoljda.filter((u) => u.behall === false).length
  const obesvarade = uppfoljda.length - behallna - avslutade

  const nyaMal = nyaMalVid(b, alla.map((r) => r.besok))

  const rader: string[] = []

  if (harNytta) {
    if (samtalslage) {
      rader.push('Nyttofrågor ställdes. Resultatet visas inte i samtalsläge.')
    } else if (vagtMot) {
      const utfall =
        b.index_total !== null ? `Målindex blev ${b.index_total.toFixed(1)}.` : 'Målindex kunde inte beräknas.'
      rader.push(
        `Nyttofrågor ställdes och vägdes mot ${vagtMot.etikett.toLowerCase()} ${formateraDatum(
          vagtMot.besok.created
        )}. ${utfall}`
      )
    } else {
      rader.push('Nyttofrågor ställdes, men det fanns ingen tidigare behovsskattning att väga mot.')
    }
  }

  if (uppfoljda.length > 0) {
    const delar = [`${behallna} behölls`, `${avslutade} avslutades`]
    if (obesvarade > 0) delar.push(`${obesvarade} obesvarade`)
    rader.push(`${uppfoljda.length} mål följdes upp — ${delar.join(', ')}.`)
  }

  if (rad.harNyBehov) {
    rader.push('Nya behovsfrågor ställdes. De väger nästa besök.')
  } else if (harNytta) {
    rader.push('Behoven bedömdes som oförändrade – inga nya behovsfrågor ställdes.')
  } else if (!harNytta) {
    rader.push('Behovsfrågor ställdes och blev utgångsläget för kommande mätningar.')
  }

  if (nyaMal.length > 0) {
    const inledning = nyaMal.length === 1 ? '1 nytt mål tillkom' : `${nyaMal.length} nya mål tillkom`
    rader.push(`${inledning}. Gällande mål efter besöket: ${b.mal?.length ?? 0}.`)
  }

  const svarandeText = b.svarande ? SVARANDE_ETIKETT[b.svarande] ?? b.svarande : null
  const metaDelar: string[] = []
  if (svarandeText) metaDelar.push(`Svar lämnade av ${svarandeText}.`)
  metaDelar.push(b.process_id ? `ÄrendeID ${b.process_id}.` : 'ÄrendeID saknas.')

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-gray-100 pt-3 text-base text-gray-700">
      {rader.map((r, i) => (
        <p key={i} className="m-0">
          {r}
        </p>
      ))}

      {(b.mal?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-1">
          {b.mal.map((m, i) => {
            const u = uppfoljda.find((x) => x.malId === m.malId)
            // Numret bara när det finns fler än ett mål. "Mål 1" när det är det enda
            // målet läser sig som att det saknas ett till.
            const etikett = b.mal.length > 1 ? `Mål ${i + 1}` : 'Mål'
            return (
              <p key={m.malId} className="m-0 text-sm">
                <span className="font-medium text-gray-700">{etikett}: </span>
                <span className="text-gray-800">{maltextFor(m)}</span>
                {u && u.behall !== null && (
                  <span className="text-gray-500"> — {u.behall ? 'behålls' : 'avslutat'}</span>
                )}
              </p>
            )
          })}
        </div>
      )}

      {b.note && <p className="m-0 text-sm italic text-gray-500">Anteckning: {b.note}</p>}

      <p className="m-0 text-sm text-gray-500">{metaDelar.join(' ')}</p>

      {/* Utfällningen svarar på "vad hände här?" i prosa. Svaren område för område och
          underlaget till beställningen ryms inte – de bor i besöksvyn. */}
      <button
        type="button"
        onClick={() => onOppna(b.id)}
        className="self-start text-base text-pejlen-500 transition-colors hover:text-pejlen-400"
      >
        Öppna besöket <RightOutlined className="text-xs" />
      </button>
    </div>
  )
}

const BesokLista: React.FC<{
  besok: MalindexBesok[]
  samtalslage: boolean
  /** Pausat besök, om det finns ett. Blockerar nya besök tills det är hanterat. */
  utkast: MalindexBesok | null
  onKompletteraProcessId: (id: number, processId: string) => Promise<void>
  onNyttKartlaggningsbesok: () => void
  onNyUppfoljning: () => void
  onAterupptaUtkast: () => void
  onRaderaBesok: () => void
  onOppnaBesok: (id: number) => void
}> = ({
  besok,
  samtalslage,
  utkast,
  onKompletteraProcessId,
  onNyttKartlaggningsbesok,
  onNyUppfoljning,
  onAterupptaUtkast,
  onRaderaBesok,
  onOppnaBesok,
}) => {
  const [oppna, setOppna] = React.useState<number[]>([])
  const rader = annotera(besok)
  // Utkastet räknas inte som "senaste besöket" – ingenting har hänt med det ännu.
  const avslutade = rader.filter((r) => !r.besok.draft)
  const senaste = avslutade.length > 0 ? avslutade[avslutade.length - 1] : null
  // Besök som startar om från början. Utan avdelaren finns ingen förklaring i vyn
  // till varför målen tog slut mellan två rader.
  const brytningar = periodbrytningar(besok)

  const toggla = (id: number) => setOppna((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]))

  return (
    <div className="flex flex-col gap-4">
      {/* Ingen tom-ruta här – "Börja här" nedanför säger redan vad som ska göras. */}
      {rader.length > 0 && (
        <div className="flex flex-col gap-2">
          {rader.map((rad) => {
            const b = rad.besok
            const utfalld = oppna.includes(b.id)
            const harNytta = !!b.nytta_svar && Object.keys(b.nytta_svar).length > 0

            return (
              <React.Fragment key={b.id}>
                {brytningar.has(b.id) && (
                  <div className="flex items-center gap-3 py-3" aria-hidden={false}>
                    <span className="h-px flex-1 bg-gray-200" />
                    <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-gray-400">
                      Ny kartläggning — börjar om
                    </span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </div>
                )}
              <div className="rounded-lg border border-gray-100 bg-white px-3 py-2.5 text-base">
                {/*
                  Rad 1 har alltid samma form: etikett, tidpunkt, antal mål, Målindex.
                  Ingenting läggs till eller tas bort här beroende på om något saknas –
                  då skulle pelarna flytta sig mellan raderna. Avvikelser hamnar på rad 2.
                */}
                <button
                  type="button"
                  aria-expanded={utfalld}
                  onClick={() => toggla(b.id)}
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 text-left"
                >
                  {utfalld ? (
                    <DownOutlined className="text-xs text-gray-400" />
                  ) : (
                    <RightOutlined className="text-xs text-gray-400" />
                  )}
                  {/* Fasta minimibredder håller pelarna i linje mellan raderna. Bredderna
                      rymmer det längsta innehållet: "Uppföljande telefonsamtal" och
                      "2026-08-19 11:24". */}
                  <span className="font-medium text-gray-900" style={{ minWidth: 235 }}>
                    {rad.etikett}
                  </span>
                  <span className="text-gray-600" style={{ minWidth: 155 }}>
                    {formateraDatumTid(b.created)}
                  </span>
                  {/* Tankstreck, inte tomt, när inga mål formulerades. En tom cell
                      läses som att siffran fattas; strecket säger att svaret är noll. */}
                  <span className="text-gray-600" style={{ minWidth: 64 }}>
                    {rad.formuleradeMal > 0 ? `${rad.formuleradeMal} mål` : <span className="text-gray-300">—</span>}
                  </span>

                  <span className="ml-auto shrink-0 text-gray-500">
                    {samtalslage ? (
                      <span className="text-gray-300">
                        <EyeInvisibleOutlined />
                      </span>
                    ) : b.index_total !== null ? (
                      <span>
                        Målindex <span className="font-semibold text-gray-800">{b.index_total.toFixed(1)}</span>
                      </span>
                    ) : harNytta && !b.draft ? (
                      // Ett utkast får aldrig kallas "ej beräkningsbart". Det är inte
                      // omöjligt att räkna ut – besöket är bara inte klart.
                      <span className="italic text-gray-400">ej beräkningsbart</span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </span>
                </button>

                {/*
                  Rad 2 – bara när något avviker. Egen rad så att rad 1 aldrig byter form.
                  Listan är avsedd att växa: utkast, gamla pausade besök, saknade fält.
                */}
                {(b.draft || (!b.draft && !b.process_id)) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {b.draft && (
                      <span className="rounded-sm border border-amber-300 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-800">
                        Utkast
                      </span>
                    )}
                    {/* ÄrendeID efterfrågas först när besöket sparas på riktigt. Att
                        skälla på ett utkast för att det saknas vore fel – det är inte
                        dags än. */}
                    {!b.draft && !b.process_id && (
                      <KompletteraProcessId besokId={b.id} onSpara={onKompletteraProcessId} />
                    )}
                  </div>
                )}

                {utfalld && (
                  <BesokDetaljer rad={rad} alla={rader} samtalslage={samtalslage} onOppna={onOppnaBesok} />
                )}
              </div>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {/*
        Nästa steg ligger där tidslinjen tar slut – det finns alltid bara ett rimligt.

        Finns ett pausat besök är det DET som är nästa steg, och start-knapparna
        försvinner helt. Handläggaren måste ta ställning till utkastet innan ett nytt
        besök kan börja: annars ligger ett halvfärdigt samtal kvar som ingen minns,
        och två parallella besök för samma person skapar frågan om vilket som gäller.
      */}
      {utkast ? (
        <div className="rounded-lg border border-dashed border-amber-300 px-3 py-3">
          <p className="m-0 text-base font-medium text-gray-900">Ett besök är pausat</p>
          <p className="mt-0.5 max-w-2xl text-sm text-gray-700">
            Besöket som påbörjades {formateraDatum(utkast.created)} är inte slutfört. Återuppta det eller radera
            besöket. Nya besök kan inte startas innan detta är hanterat.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onAterupptaUtkast}
              className="rounded-sm bg-pejlen-500 px-4 py-2 text-base text-white transition-colors hover:bg-pejlen-400"
            >
              Återuppta besöket
            </button>
            <button
              type="button"
              onClick={onRaderaBesok}
              className="rounded-sm border border-gray-300 px-4 py-2 text-base text-gray-600 transition-colors hover:border-red-400 hover:text-red-600"
            >
              Radera besöket
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-pejlen-300 px-3 py-3">
          <p className="m-0 text-base font-medium text-gray-900">{senaste ? 'Nästa steg' : 'Börja här'}</p>
          <p className="mt-0.5 max-w-2xl text-sm text-gray-600">
            {senaste
              ? `Följ upp målen och mät nyttan. Nyttan vägs mot behovsskattningen från ${formateraDatum(
                  senaste.besok.created
                )}.`
              : 'Ingen kartläggning gjord än. Gå igenom behoven och formulera mål tillsammans med brukaren.'}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {senaste && (
              <button
                type="button"
                onClick={onNyUppfoljning}
                className="rounded-sm bg-pejlen-500 px-4 py-2 text-base text-white transition-colors hover:bg-pejlen-400"
              >
                Ny uppföljning
              </button>
            )}
            {/* Alltid tillganglig, men nedtonad nar historik finns - uppfoljningen tar
                redan ny behovsbild, sa en ny kartlaggning behovs sallan. */}
            <button
              type="button"
              onClick={onNyttKartlaggningsbesok}
              className={
                senaste
                  ? 'rounded-sm border border-gray-300 px-4 py-2 text-base text-gray-600 transition-colors hover:border-pejlen-400 hover:text-pejlen-500'
                  : 'rounded-sm bg-pejlen-500 px-4 py-2 text-base text-white transition-colors hover:bg-pejlen-400'
              }
            >
              Ny kartläggning
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const MalindexOverTid: React.FC<{ besok: MalindexBesok[] }> = ({ besok }) => {
  const punkter = indexUtveckling(besok)

  if (punkter.length === 0) {
    return (
      <Empty description="Inget beräknat Målindex än – linjen visas när ett uppföljningsbesök sparats mot en tidigare behovsskattning." />
    )
  }

  // En färg per omgång. Kartläggningen har ingen egen punkt, så färgskiftet är det som
  // visar var behovsbilden togs om. Linjen klipps inte av – talen är jämförbara.
  const perioder = periodNummer(besok)
  const periodFor = punkter.map((p) => perioder.get(p.besokId) ?? 0)
  const antalPerioder = periodFor.length > 0 ? Math.max(...periodFor) + 1 : 1
  const fargFor = (i: number) => PERIODFARGER[i % PERIODFARGER.length]

  const kalla =
    punkter.length === 1
      ? `Ett värde, från uppföljningen ${formateraDatum(punkter[0].datum)}.`
      : `${punkter.length} värden, från ${formateraDatum(punkter[0].datum)} till ${formateraDatum(
          punkter[punkter.length - 1].datum
        )}. Varje punkt är nyttan vägd mot den behovsskattning som gällde då.` +
        (antalPerioder > 1
          ? ' Färgen byts vid varje ny kartläggning: behovsbilden är omtagen där, så ett hopp över färgskiftet kan bero på nya svar snarare än på en förändring.'
          : '')
  const options: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `Målindex: ${Number(c.parsed.y).toFixed(1)}` } },
    },
    scales: { y: { min: 0, max: 4, ticks: { stepSize: 1 }, title: { display: true, text: 'Målindex' } } },
  }

  return (
    <>
      <GrafRubrik titel="Målindex över tid" />
      <div className="relative h-64 w-full sm:h-72">
        <Line
        options={options}
        data={{
          labels: punkter.map((p) => formateraDatum(p.datum)),
          datasets: [
            {
              label: 'Målindex',
              data: punkter.map((p) => p.index),
              // Sträckan tillhör punkten den leder FRAM till, så färgen byter vid den
              // första punkten i en ny omgång.
              borderColor: fargFor(periodFor[periodFor.length - 1]).border,
              segment: { borderColor: (ctx) => fargFor(periodFor[ctx.p1DataIndex]).border },
              pointBackgroundColor: periodFor.map((i) => fargFor(i).border),
              pointBorderColor: periodFor.map((i) => fargFor(i).border),
              pointRadius: 4,
              pointHoverRadius: 6,
              tension: 0.25,
              fill: false,
            },
          ],
          }}
        />
      </div>

      {/* Legenden bara när det finns mer än en omgång – annars namnger den något som
          inte behöver namnges. */}
      {antalPerioder > 1 && (
        <GrafLegend
          poster={Array.from({ length: antalPerioder }, (_, i) => {
            const forsta = punkter.find((_, k) => periodFor[k] === i)
            return {
              farg: fargFor(i).fill,
              kant: fargFor(i).border,
              text: forsta ? `Omgång ${i + 1}, från ${formateraDatum(forsta.datum)}` : `Omgång ${i + 1}`,
            }
          })}
        />
      )}

      <GrafKalla text={kalla} />
    </>
  )
}

/**
 * Talen ovanför staplarna.
 *
 * Fyra tillstånd ska gå att skilja åt, och tre av dem saknar stapelhöjd: noll är ett
 * riktigt svar ("inte alls viktigt"), ej tillämpligt är inget svar alls, och obesvarat
 * är en tredje sak. Utan utskrivna tal ser de likadana ut – tomt.
 *
 *   0      talet 0 på nollinjen
 *   1–4    talet ovanför stapeln
 *   ET     ett kryss på nollinjen
 *   tomt   ingenting
 *
 * Egen ritfunktion i stället för chartjs-plugin-datalabels: Pejlen registrerar
 * Chart.js-plugins globalt, så ett datalabels-plugin hade satt siffror på ett tjugotal
 * benchmarking-grafer på köpet. Den här skickas bara till den här grafen.
 */
const KRYSS = '×'

const vardeEtiketter = (rader: BehovNyttaRad[]): Plugin<'bar'> => ({
  id: 'malindexVardeEtiketter',
  afterDatasetsDraw(chart) {
    const { ctx } = chart
    const yAxeln = chart.scales.y
    if (!yAxeln) return
    const nollinjen = yAxeln.getPixelForValue(0)
    if (!Number.isFinite(nollinjen)) return

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.font = `600 12px ${ChartJS.defaults.font.family}`

    // Svart, inte staplarnas färg. Etiketten sitter direkt ovanför sin stapel, så
    // färgkodning behövs inte för att para ihop dem – och två blekare toner bredvid
    // varandra läses sämre än en tydlig.
    ctx.fillStyle = '#262626'

    chart.data.datasets.forEach((_dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (meta.hidden) return

      meta.data.forEach((element, index) => {
        const rad = rader[index]
        if (!rad || !Number.isFinite(element.x)) return

        const varde = datasetIndex === 0 ? rad.behov : rad.nytta
        const ejTillampligt = datasetIndex === 0 ? rad.behovEt : rad.nyttaEt

        // Obesvarat får ingen markering – det finns inget att skriva ut.
        if (varde === null && !ejTillampligt) return

        const text = varde !== null ? String(varde) : KRYSS
        // Nollan har ingen stapel att sitta ovanpå, så den hamnar på nollinjen.
        const topp = varde !== null && varde > 0 && Number.isFinite(element.y) ? element.y : nollinjen
        ctx.fillText(text, element.x, topp - 4)
      })
    })

    ctx.restore()
  },
})

/**
 * Behov och nytta per område.
 *
 * Staplarna kommer ur det par som faktiskt producerade det senaste Målindexet –
 * inte ur senaste behov och senaste nytta var för sig. Ett uppföljningsbesök tar
 * ofta en ny behovsbild, och den har ännu inte viktat något index; den väger
 * nästa besök. Att para ihop dem skulle visa två mätningar som inte hör ihop och
 * som inte stämmer med talet i besökslistan.
 *
 * Bara den PÅGÅENDE perioden. Efter en ny kartläggning visades annars förra omgångens
 * par vidare, ända till nästa uppföljning – rubriken sa "senaste" till någon som just
 * börjat om.
 */
const BehovOchNytta: React.FC<{ config: MalindexConfig; besok: MalindexBesok[] }> = ({ config, besok }) => {
  const period = pagaendePeriod(besok)
  const par = senasteIndexPar(period)

  if (!par) {
    // Har det gjorts besök före den här perioden är tomheten en följd av omstarten,
    // inte av att ingenting hänt. Det är två olika saker för den som läser.
    const efterOmstart = period.length < besok.length
    return (
      <Empty
        description={
          efterOmstart
            ? 'Ingen jämförelse i den pågående perioden än. Kartläggningen gav en ny behovsbild – jämförelsen kommer efter nästa uppföljning.'
            : 'Ingen jämförelse än – den visas när ett uppföljningsbesök gett ett beräknat Målindex.'
        }
      />
    )
  }

  const rader = behovVsNytta(config.omraden, par.behov, par.nytta, config.ejTillampligt.varde)
  const etikettEt = config.ejTillampligt.etikett

  // Är varje område markerat "ej tillämpligt" finns ingenting att rita. Tio tomma
  // platser säger mindre än en mening som förklarar varför de är tomma.
  if (rader.every((r) => r.behov === null && r.nytta === null))
    return <Empty description={`Samtliga områden är markerade ${etikettEt.toLowerCase()} – ingen jämförelse att visa.`} />

  const options: ChartOptions<'bar'> = {
    maintainAspectRatio: false,
    responsive: true,
    // Hela kolumnen är träffyta, inte stapeln. Annars går ett område med noll eller
    // ej tillämpligt inte att peka på – det finns ingen stapel att träffa.
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (c) => {
            const rad = rader[c.dataIndex]
            const ejTillampligt = c.datasetIndex === 0 ? rad?.behovEt : rad?.nyttaEt
            return `${c.dataset.label}: ${ejTillampligt ? etikettEt : c.parsed.y ?? '–'}`
          },
        },
      },
    },
    scales: {
      /*
       * Ingen y-axel. Varje stapel bär sitt eget tal, så en axel att läsa av mot vore
       * en andra uppsättning siffror för samma sak – och utan axel har stödlinjerna
       * inget att peka på. Skalan går till 5 för att lämna luft åt etiketten ovanför
       * en fyra; taket ritas aldrig ut.
       */
      y: { min: 0, max: 5, display: false },
      // De lodräta linjerna står kvar: de skiljer områdena åt, och när de vågräta
      // försvinner är de det enda som håller ihop paret behov–nytta i sin ruta.
      x: {},
    },
  }

  const behovDatum = formateraDatum(par.behov.created)
  const nyttaDatum = formateraDatum(par.nytta.created)

  return (
    <>
      <GrafRubrik titel="Behov och nytta per område" />
      <div className="relative h-72 w-full sm:h-80">
        <Bar
          options={options}
          plugins={[vardeEtiketter(rader)]}
          data={{
            labels: rader.map((r) => r.namn),
            /*
             * Hela ordet här, inte "Behov" och "Nytta". Legenden och tooltipen namnger två
             * skattningar som gjordes vid skilda tillfällen – de korta orden lät som två
             * kolumner i en tabell. Rubrikerna och fliken behåller kortformen: där räcker
             * det att säga vad grafen handlar om.
             *
             * Serienamnen bär däremot inget datum. De syns bara i tooltipen, och där står
             * redan områdets namn som rubrik – "Behov 2026-09-10: 0" blev tre uppgifter på
             * en rad där en behövdes. Datumen hör hemma i legenden.
             */
            datasets: [
              {
                label: 'Behovsskattning',
                data: rader.map((r) => r.behov),
                backgroundColor: BLUE.fill,
                borderColor: BLUE.border,
                borderWidth: 1,
              },
              {
                label: 'Nyttoskattning',
                data: rader.map((r) => r.nytta),
                backgroundColor: GREEN.fill,
                borderColor: GREEN.border,
                borderWidth: 1,
              },
            ],
          }}
        />
      </div>

      {/* Ingen källtext under den här grafen. Legendens två datum säger vilka mätningar
          som jämförs, och det räckte – meningen under upprepade samma sak i löptext. */}
      <GrafLegend
        poster={[
          { farg: BLUE.fill, kant: BLUE.border, text: `Behovsskattning ${behovDatum}` },
          { farg: GREEN.fill, kant: GREEN.border, text: `Nyttoskattning ${nyttaDatum}` },
        ]}
      />
    </>
  )
}

const OversiktHub: React.FC<OversiktHubProps> = ({
  config,
  besok,
  samtalslage,
  onNyttKartlaggningsbesok,
  onNyUppfoljning,
  onAterupptaUtkast,
  onRaderaBesok,
  onOppnaBesok,
  onKompletteraProcessId,
}) => {
  const mal = aktivaMal(besok)
  const [visaNyKartlaggning, setVisaNyKartlaggning] = React.useState(false)
  const [visaGammaltUtkast, setVisaGammaltUtkast] = React.useState(false)
  const [visaRaderaBesok, setVisaRaderaBesok] = React.useState(false)

  // Bara ett utkast åt gången kan finnas – flödet hindrar att ett andra startas.
  const utkast = besok.find((b) => b.draft) ?? null
  const utkastAlder = utkast ? utkastAlderIDagar(utkast.created) : 0

  /**
   * Gamla utkast går via en varning. Svaren i dem beskriver hur det kändes då, och
   * efter ett par veckor kan de ha slutat gälla utan att det syns på skärmen.
   */
  const aterupptaUtkast = () => {
    if (utkastAlder >= GAMMALT_UTKAST_DAGAR) setVisaGammaltUtkast(true)
    else onAterupptaUtkast()
  }

  /**
   * Grinden framför "Ny kartläggning". Gäller så snart det finns historik, oavsett om
   * det finns mål att förlora.
   *
   * Villkoret var först "bara när mål finns", eftersom en omstart utan mål inte kostar
   * något. Men då kom rutan ibland och ibland inte, och en knapp som beter sig olika
   * från gång till gång är svårare att lita på än en som alltid frågar. Omstarten syns
   * dessutom i tidslinjen och i grafernas färger även när inga mål går förlorade.
   *
   * Första kartläggningen för en brukare passerar utan fråga – det finns ingenting att
   * börja om från.
   */
  const startaKartlaggning = () => {
    if (raknade(besok).length > 0) setVisaNyKartlaggning(true)
    else onNyttKartlaggningsbesok()
  }

  return (
    <div>
      <NyKartlaggningModal
        open={visaNyKartlaggning}
        personnummer={besok[0]?.personnummer ?? ''}
        aktivaMal={mal}
        onBekrafta={() => {
          setVisaNyKartlaggning(false)
          onNyttKartlaggningsbesok()
        }}
        onAvbryt={() => setVisaNyKartlaggning(false)}
      />

      <GammaltUtkastModal
        open={visaGammaltUtkast}
        alder={utkastAlder}
        onAterupptaAnda={() => {
          setVisaGammaltUtkast(false)
          onAterupptaUtkast()
        }}
        onBorjaOm={() => {
          setVisaGammaltUtkast(false)
          onRaderaBesok()
        }}
        onAvbryt={() => setVisaGammaltUtkast(false)}
      />

      {/* Bekräftelsen gäller BARA knappen i utkastrutan. Vägen via "Utkastet är gammalt"
          har redan ställt frågan en gång, och två rutor i rad om samma sak är tjat. */}
      <RaderaBesokModal
        open={visaRaderaBesok}
        datum={utkast ? formateraDatum(utkast.created) : ''}
        onBekrafta={() => {
          setVisaRaderaBesok(false)
          onRaderaBesok()
        }}
        onAvbryt={() => setVisaRaderaBesok(false)}
      />

      {/*
        Att brukaren SAKNAR mål syntes tidigare bara genom att rutan uteblev, och en
        tom yta säger ingenting. Målen är det man arbetar mot – att stå utan är ett
        läge som ska gå att se, och det ska framgå hur man tar sig ur det.

        Vägen dit är uppföljningen, inte en ny kartläggning: målformuleringen ligger
        sist i behovsspåret och nås därifrån, medan en kartläggning dessutom bryter
        omgången i onödan.

        Visas inte för en brukare utan besök – då säger "Börja här" redan vad som gäller.
      */}
      {mal.length === 0 && raknade(besok).length > 0 && !utkast && (
        <div className="mb-4 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Inga aktiva mål</p>
          <p className="m-0 mt-1 text-base text-gray-700">
            Brukaren har inget mål att arbeta mot. Nya mål sätts i en uppföljning — svara ja på frågan om behoven
            förändrats, så kommer ni till målformuleringen.
          </p>
        </div>
      )}

      {/* Målen först – de är det brukaren och handläggaren faktiskt arbetar mot. */}
      {mal.length > 0 && (
        <div className="mb-4 max-w-3xl rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Aktiva mål ({mal.length})</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {mal.map((m, i) => (
              <li key={m.malId} className="text-base text-gray-800">
                {/* Samma regel som i besökets expandering: numret bara när det finns
                    fler än ett mål. */}
                <span className="font-medium text-gray-700">{mal.length > 1 ? `Mål ${i + 1}` : 'Mål'}: </span>
                {maltextFor(m)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Tabs defaultActiveKey="besok">
        <TabPane tab="Besök" key="besok">
          <BesokLista
            besok={besok}
            samtalslage={samtalslage}
            utkast={utkast}
            onKompletteraProcessId={onKompletteraProcessId}
            onNyttKartlaggningsbesok={startaKartlaggning}
            onNyUppfoljning={onNyUppfoljning}
            onAterupptaUtkast={aterupptaUtkast}
            onRaderaBesok={() => setVisaRaderaBesok(true)}
            onOppnaBesok={onOppnaBesok}
          />
        </TabPane>

        <TabPane tab="Målindex över tid" key="overTid">
          {samtalslage ? <DoltISamtalslage vad="Målindex över tid" /> : <MalindexOverTid besok={besok} />}
        </TabPane>

        <TabPane tab="Behov och nytta" key="behovOchNytta">
          {samtalslage ? <DoltISamtalslage vad="Behov och nytta per område" /> : <BehovOchNytta config={config} besok={besok} />}
        </TabPane>
      </Tabs>
    </div>
  )
}

export default OversiktHub
